"use client";

import { useCallback } from "react";
import { create } from "zustand";
import { useQueryClient } from "@tanstack/react-query";
import {
  useSelectProject,
  getGetProjectQueryKey,
} from "@/lib/api/generated";
import type { ProjectResponse } from "@/lib/api/model";
import { useOptionalTalentSearch } from "@/components/search/talent-search-provider";
import { useOptionalTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import { useArbitratorViewStore } from "@/features/arbitrator/store/view-store";
import { useJobListStore } from "@/features/jobs/store/job-store";

interface ProjectSwitchState {
  isSwitching: boolean;
  /** When true the dashboard layout shows the full-screen PageLoader. */
  showLoader: boolean;
  pendingProject: Pick<ProjectResponse, "id" | "title" | "cover_image_url"> | null;
}

export const useProjectSwitchStore = create<ProjectSwitchState>(() => ({
  isSwitching: false,
  showLoader: false,
  pendingProject: null,
}));

/**
 * Query key prefixes that should NOT be reset on project switch — they are
 * org/user-scoped, not project-scoped.
 */
const KEEP_PREFIXES = [
  "auth",
  "billing",
  "autocomplete",
  "/api/auth",
  "/api/settings",
  "/api/org",
  "/api/onboarding",
  "/api/connected-accounts",
  "/api/notifications",
  "/api/invitations",
  "/api/public",
  "/api/snippets",
  "/api/presets",
];

function isProjectScoped(queryKey: readonly unknown[]): boolean {
  const first = queryKey[0];
  if (typeof first !== "string") return true;
  return !KEEP_PREFIXES.some((p) => first.startsWith(p));
}

interface UseProjectSwitchOptions {
  onSuccess?: () => void | Promise<void>;
  onError?: (err: Error) => void;
}

/**
 * Central hook for switching the active project.
 *
 * Project-scoped queries are **reset** (not removed) so they return to their
 * initial loading state — components show skeletons instead of flashing stale
 * data. Org/user-scoped caches (billing, notifications, etc.) are untouched.
 */
export function useProjectSwitch(options?: UseProjectSwitchOptions) {
  const queryClient = useQueryClient();
  const talentSearch = useOptionalTalentSearch();
  const talentSearchFilters = useOptionalTalentSearchFilters();

  const mutation = useSelectProject<Error>({
    mutation: {
      onMutate: () => {
        useProjectSwitchStore.setState({ isSwitching: true });
      },
      onSuccess: async (_data, { projectId }) => {
        // 1. Reset React context state bound to the old project
        talentSearch?.resetSearch();
        talentSearchFilters?.resetFilters();

        // 2. Reset Zustand stores that cache project-scoped data
        const viewStore = useArbitratorViewStore.getState();
        viewStore.clearSelected();
        viewStore.closeReview();
        viewStore.closeSetup();
        useArbitratorViewStore.setState({
          expanded: new Set(),
          sessionIdByJobStage: {},
          sessionMetaByJobStage: {},
        });
        useJobListStore.getState().resetFilters();

        // 3. Optimistically patch auth so useAuth().selectedProjectId updates
        //    synchronously — no gap where it's null.
        queryClient.setQueryData(
          ["auth", "me"],
          (old: Record<string, unknown> | undefined) =>
            old ? { ...old, selected_project_id: projectId } : old,
        );

        // 4. Seed the new project's detail cache from the mutation response so
        //    useSelectedProject() resolves immediately.
        if (_data) {
          queryClient.setQueryData(getGetProjectQueryKey(projectId), _data);
        }

        // 5. Reset every project-scoped query to its initial loading state.
        //    Unmounted queries won't refetch until their component mounts.
        await queryClient.resetQueries({
          predicate: (query) => isProjectScoped(query.queryKey),
        });

        // 6. Navigate first — the PageLoader covers the transition.
        await options?.onSuccess?.();

        useProjectSwitchStore.setState({
          isSwitching: false,
          showLoader: false,
          pendingProject: null,
        });
      },
      onError: (err) => {
        useProjectSwitchStore.setState({
          isSwitching: false,
          showLoader: false,
          pendingProject: null,
        });
        options?.onError?.(err);
      },
    },
  });

  const switchProject = useCallback(
    (
      projectId: string,
      optimistic?: Pick<ProjectResponse, "id" | "title" | "cover_image_url">,
    ) => {
      if (optimistic) {
        useProjectSwitchStore.setState({ pendingProject: optimistic });
      }
      mutation.mutate({ projectId });
    },
    [mutation],
  );

  const switchProjectAsync = useCallback(
    (
      projectId: string,
      optimistic?: Pick<ProjectResponse, "id" | "title" | "cover_image_url">,
    ) => {
      if (optimistic) {
        useProjectSwitchStore.setState({ pendingProject: optimistic });
      }
      return mutation.mutateAsync({ projectId });
    },
    [mutation],
  );

  return {
    switchProject,
    switchProjectAsync,
    isPending: mutation.isPending,
  };
}
